import { FOIL_LIGHTING_GLSL } from './foilLighting.js';

// Shared GLSL for the premium WebGL foil. Prototype lives on the card
// inspector; see docs/plans/foil-webgl-shader.md. All look parameters are
// uniforms (u<Key>) driven by foilParams.js so the dev panel can tune live.
//
// Realism stack: parallax-occlusion relief, procedural studio-environment
// reflection, GGX/Cook-Torrance metallic specular with Schlick Fresnel,
// luminance-keyed metal (light areas = foil, dark = matte), iridescence,
// directional sparkle, zone emboss. Static at rest, motion only on tilt.

export const FOIL_VERTEX_SHADER = /* glsl */ `
  varying vec2 vUv;
  // Tangent-space view + light directions for the board (uSceneLit=1). The
  // front cap's UV maps U->local +X, V->local +Y, face normal->local +Z (see
  // cardMesh generateTopUV), so the card's tangent frame in world space is the
  // model matrix applied to those local axes. We pass V and L already expressed
  // in that tangent basis so the fragment shader can light the relief normal N
  // (also z-up tangent space) directly, exactly like the DOM path. Only now
  // the basis tracks the REAL camera + scene light. Unused when uSceneLit=0.
  varying vec3 vViewTS;
  varying vec3 vLightTS;
  uniform vec3 uLightDir;   // world-space direction TOWARD the light, normalized
  void main() {
    vUv = uv;
    vec3 worldPos = (modelMatrix * vec4(position, 1.0)).xyz;
    vec3 T = normalize(mat3(modelMatrix) * vec3(1.0, 0.0, 0.0));
    vec3 B = normalize(mat3(modelMatrix) * vec3(0.0, 1.0, 0.0));
    vec3 Nw = normalize(mat3(modelMatrix) * vec3(0.0, 0.0, 1.0));
    vec3 viewW = normalize(cameraPosition - worldPos);
    vViewTS = vec3(dot(viewW, T), dot(viewW, B), dot(viewW, Nw));
    vec3 lW = normalize(uLightDir);
    vLightTS = vec3(dot(lW, T), dot(lW, B), dot(lW, Nw));
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

export const FOIL_FRAGMENT_SHADER = /* glsl */ `
  precision highp float;

  varying vec2 vUv;
  // Board scene-lit varyings (computed in the vertex shader from the real
  // camera + uLightDir). Used only when uSceneLit > 0.5; harmless otherwise.
  varying vec3 vViewTS;
  varying vec3 vLightTS;
  uniform float uSceneLit;       // 0 = DOM self-lit (default), 1 = board scene-lit
  uniform vec3 uLightColor;      // scene directional light rgb * intensity
  uniform vec3 uAmbient;         // scene ambient rgb * intensity
  uniform sampler2D uTex;

  uniform sampler2D uFoilMap;   // baked RG=normal grad, B=height, A=white mask
  uniform float uHasFoilMap;    // 1 if the baked map is loaded, else derive
  uniform sampler2D uDepthMap;  // baked monocular depth, R = near(1)..far(0)
  uniform float uHasDepthMap;   // 1 if the baked depth map is loaded, else estimate
  uniform vec2 uTexel;
  uniform vec2 uTilt;
  uniform float uFromCenter;
  uniform float uTime;
  uniform vec4 uZone0;
  uniform vec4 uZone1;

  // Tunables (foilParams.js)
  uniform float uPomOn, uPomDepth, uReliefStrength, uNormalBlur;
  uniform float uDepthReliefStrength;

  uniform float uEmbossOn;

  uniform float uRestLightX, uRestLightY;

  // Rounded-corner coverage (UV-fraction radius). 0 = no rounding (default,
  // used by the inspector / hand / board where the DOM clips the canvas). The
  // shared FoilStage sets this > 0 because its single canvas spans the whole
  // grid viewport and can't rely on per-tile CSS border-radius to clip quads.
  uniform float uCornerRadius;
  // Quad height / width. The corner SDF runs in width units so the radius
  // is circular on screen (in raw UV a portrait quad rounds taller than wide).
  uniform float uQuadAspect;
  uniform float uUseTexAlpha;
  // Whole-quad fade. Lets a caller cross-fade an individual foil card in or out
  // WITH its foil effect intact, instead of fading a separate layer over it.
  // Defaults to 1 everywhere; only the deck-builder grid's entrance drives it.
  uniform float uOpacity;
  // Object-cover sampling crop (CHANGE 1): the stage quad's aspect can differ
  // from the card art's aspect (landscape sites in a portrait tile box). The
  // DOM <img> uses object-cover (center-crop); to match, the shader samples a
  // cropped sub-rect of the texture: uvc = vUv * uUvScale + uUvOffset. Default
  // (1,1)/(0,0) is an identity transform (uvc == vUv), so portrait cards and
  // every non-stage caller are byte-identical to before.
  uniform vec2 uUvScale;
  uniform vec2 uUvOffset;
  // Site rotation: site art is stored portrait but displayed rotated 90deg CW
  // (so it reads horizontally), matching the DOM img transform rotate(90deg).
  // The grid (CardFoilScene) rotates its quad mesh instead, so it leaves this 0;
  // the single-quad surfaces (inspector + hover previews) keep their inset:0
  // canvas and set this 1, rotating the SAMPLED texture so the foil overlay
  // matches the rotated image underneath instead of showing portrait art sideways.
  uniform float uRotate90;
  // Board parity: 1 = sRGB-decode the final color to cancel the scene
  // renderer's sRGB output-encode (so the raw foil math reaches screen exactly
  // like the deck builder). DOM callers leave 0 (they output LinearSRGB).
  uniform float uOutputLinear;

  ${FOIL_LIGHTING_GLSL}

  vec2 imgSpace(vec2 uv) { return vec2(uv.x, 1.0 - uv.y); }
  float zoneSDF(vec2 p, vec4 r) {
    vec2 c = r.xy + r.zw * 0.5;
    vec2 d = abs(p - c) - r.zw * 0.5;
    return max(d.x, d.y);
  }
  float heightAt(vec2 uv) {
    // Height for POM is derived live from luminance (cheap, 1 tap). The foil
    // map's B channel now holds the white-mask, not height.
    float l = foilLuma(texture2D(uTex, uv).rgb);
    vec2 ip = imgSpace(uv);
    float zb = smoothstep(0.004, -0.012, min(zoneSDF(ip, uZone0), zoneSDF(ip, uZone1)));
    return l * 0.7 + zb * 0.3;
  }

  vec2 parallaxUV(vec2 uv, vec2 viewDir) {
    const int STEPS = 10;
    vec2 delta = viewDir * uPomDepth / float(STEPS);
    float layerStep = 1.0 / float(STEPS);
    float curLayer = 0.0;
    vec2 cur = uv;
    float h = heightAt(cur);
    for (int i = 0; i < STEPS; i++) {
      if (curLayer >= 1.0 - h) break;
      cur -= delta;
      h = heightAt(cur);
      curLayer += layerStep;
    }
    return cur;
  }

  void main() {
    // Object-cover sampling coordinate (CHANGE 1). uvc crops the texture to the
    // quad aspect; default uUvScale=(1,1)/uUvOffset=(0,0) makes uvc == vUv, so
    // non-stage callers and portrait cards are unchanged. Every card-art /
    // foilmap / Sobel / POM / zone sample below flows from uv0 = uvc, so the
    // whole effect crops together. (The rounded-corner coverage at the end
    // stays on raw vUv. That is geometry coverage, not a texture sample.)
    // Rotate the sampling coord 90deg CW for sites (screen-right shows image-top)
    // BEFORE the object-cover crop, so the portrait texture fills the landscape
    // quad rotated, exactly like the rotated DOM <img>. Identity when uRotate90=0.
    vec2 ruv = (uRotate90 > 0.5) ? vec2(1.0 - vUv.y, vUv.x) : vUv;
    vec2 uvc = ruv * uUvScale + uUvOffset;
    vec2 uv0 = uvc;

    // The foil DEPTH is expressed through the lighting + holographic sheen, NOT
    // by displacing the art (displacing a flat texture smears the disoccluded
    // band behind the subject. There is no hidden data to reveal). So the card
    // pixels stay pin-sharp here; depth feeds the relief normal (below) and the
    // holo slide so the shine wraps the subject's real 3D form and the sheen
    // parallaxes above the surface, like a real foil's refractive layer.
    vec2 puv = uv0;
    if (uPomOn > 0.5) {
      vec2 pp = parallaxUV(uv0, uTilt);
      // Keep the text zones (name plate + rules box) undisplaced so the text
      // stays sharp; they still read as raised via the bevel normal (lighting,
      // not a UV shift, so no blur).
      vec2 ip0 = imgSpace(uv0);
      float inZone = smoothstep(0.004, -0.02, min(zoneSDF(ip0, uZone0), zoneSDF(ip0, uZone1)));
      puv = mix(pp, uv0, inZone);
    }

    vec3 base = texture2D(uTex, puv).rgb;
    float Lm = foilLuma(base);

    // White/foil substrate mask (near-white, desaturated): drives the text-zone
    // foil gate + the white->rainbow recolour. Derived at runtime from the art
    // so it stays independent of the baked COVERAGE mask now packed in foilmap.b.
    float satv = max(max(base.r, base.g), base.b) - min(min(base.r, base.g), base.b);
    float whiteMask = smoothstep(0.80, 0.96, Lm) * (1.0 - smoothstep(0.08, 0.28, satv));

    // Relief normal gradient (lx, ly) from the baked map (precomputed, amplified)
    // or a runtime Sobel. The baked map B channel carries this card's per-card
    // FOIL MODE (one constant), chosen by the classifier in generate-foil-maps.js:
    //   0.0 = LIGHT  (card has bright art -> foil the bright areas)
    //   0.5 = DARK   (card is dark        -> foil only the darkest tail)
    //   1.0 = COLOUR (card is flat        -> foil by colour contrast)
    float lx, ly, foilMode;
    if (uHasFoilMap > 0.5) {
      // 5-tap blur of the baked normal so the relief reads as smooth depth,
      // not a chisel on every leaf. uNormalBlur sets the radius (texels).
      vec2 nb = uTexel * uNormalBlur;
      vec2 g = texture2D(uFoilMap, puv).rg
             + texture2D(uFoilMap, puv + vec2(nb.x, 0.0)).rg
             + texture2D(uFoilMap, puv - vec2(nb.x, 0.0)).rg
             + texture2D(uFoilMap, puv + vec2(0.0, nb.y)).rg
             + texture2D(uFoilMap, puv - vec2(0.0, nb.y)).rg;
      g /= 5.0;
      lx = ((g.x - 0.5) * 2.0) / 4.0;    // decode: matches the bake NORMAL_GAIN = 4
      ly = ((g.y - 0.5) * 2.0) / 4.0;
      foilMode = texture2D(uFoilMap, puv).b;  // per-card foil mode (constant)
    } else {
      float s = 1.5;
      lx = foilLuma(texture2D(uTex, puv + vec2(uTexel.x * s, 0.0)).rgb) - foilLuma(texture2D(uTex, puv - vec2(uTexel.x * s, 0.0)).rgb);
      ly = foilLuma(texture2D(uTex, puv + vec2(0.0, uTexel.y * s)).rgb) - foilLuma(texture2D(uTex, puv - vec2(0.0, uTexel.y * s)).rgb);
      foilMode = 0.0;  // no baked mode -> default LIGHT
    }
    // Run EXACTLY ONE mode's rule per card (decided per-card by the classifier),
    // so coverage follows the art sparsely and modes never stack into a sheet.
    // Each rule's thresholds stay live uniforms; only the classification is baked.
    float shine = foilCoverage(base, foilMode);
    // Add the subject's MACRO 3D form from the baked depth map (Depth-Anything
    // V2) on top of the foilmap's micro relief. The depth gradient is the true
    // surface slope, so folding it into (lx, ly) makes every specular / env /
    // iridescent highlight wrap the subject's real shape. The foil reads as a
    // sculpted 3D surface, not a flat sheet. WITHOUT moving any art pixel. A
    // 2-texel central difference (depth is smooth, so a wide step stays clean).
    if (uHasDepthMap > 0.5) {
      vec2 ds = uTexel * 2.0;
      float dxp = texture2D(uDepthMap, puv + vec2(ds.x, 0.0)).r;
      float dxm = texture2D(uDepthMap, puv - vec2(ds.x, 0.0)).r;
      float dyp = texture2D(uDepthMap, puv + vec2(0.0, ds.y)).r;
      float dym = texture2D(uDepthMap, puv - vec2(0.0, ds.y)).r;
      lx += (dxp - dxm) * uDepthReliefStrength;
      ly += (dyp - dym) * uDepthReliefStrength;
    }
    vec3 N = normalize(vec3(-lx * uReliefStrength, -ly * uReliefStrength, 1.0));
    float relief = clamp(length(vec2(lx, ly)) * 7.0, 0.0, 1.0);

    vec2 ip = imgSpace(puv);
    float dist = min(zoneSDF(ip, uZone0), zoneSDF(ip, uZone1));
    float bevel = smoothstep(0.0, -0.02, dist) * (1.0 - smoothstep(-0.02, -0.05, dist)) * uEmbossOn;
    float zoneHere = smoothstep(0.004, -0.02, dist);
    // Inside the text zones, allow the foil on the near-white substrate AND on
    // the embossed bevel edge (so the raised frame is iridescent too, not just
    // embossed), while still keeping it off the dark text. Outside the zones
    // this is 1.0 (art foil unchanged).
    float zoneFoil = mix(1.0, max(whiteMask, bevel), zoneHere);
    N = normalize(N + vec3(0.0, 0.0, bevel * 0.8));

    // V / Lr / H drive all of the specular, env reflection, iridescence and
    // sparkle below. Two paths, selected by uSceneLit:
    //   DOM (uSceneLit=0): the EXISTING screen-space self-lit model. Fixed
    //     head-on view, a synthetic light position from uTilt + rest light.
    //   Board (uSceneLit=1): the REAL tangent-space camera + scene light, so
    //     the highlight tracks the camera as it orbits and the scene's day/
    //     night light direction.
    // lightXY is the shared 2D "light position" the holo / env / sparkle terms
    // key off; on the board we synthesize it from the light's tangent-space XY
    // so those rainbow/flake terms still sweep, plus a small view term so the
    // hue shifts as the camera orbits.
    vec2 lightXY;
    float glareGuard;
    vec3 V, Lr, H;
    if (uSceneLit > 0.5) {
      V = normalize(vViewTS);
      Lr = normalize(vLightTS);
      H = normalize(V + Lr);
      // Hue/flake/env sweep driven by the REAL view direction (camera orbit)
      // blended with the light direction, instead of the synthetic uTilt.
      lightXY = V.xy * 1.1 + Lr.xy * 0.5;
      // NO head-on suppression on the board: cards lie flat and are viewed
      // near-normal (high V.z), so the DOM straight-on glare guard would kill
      // the foil at the typical table angle (it looked like a plain card). The
      // energy-bounded composite already prevents a white-out, so keep full.
      glareGuard = 1.0;
    } else {
      // Effective light = tilt + resting light position (so a flat card still
      // catches light from a chosen angle).
      lightXY = uTilt + vec2(uRestLightX, uRestLightY);
      // Straight-on glare guard: when the light hits dead-on (lightXY ~ 0) the
      // half-vector aligns with nearly every near-vertical relief normal at once,
      // so the broad specular + reflection wash the whole card grey. Fade those
      // two layers to ~0 in a TINY zone around lightXY=0 so that worst angle
      // becomes a brief, barely-noticeable dim instead of an ugly grey wash.
      glareGuard = smoothstep(0.0, 0.12, length(lightXY));
      V = vec3(0.0, 0.0, 1.0);
      Lr = normalize(vec3(lightXY * 2.2, 1.0));
      H = normalize(Lr + V);
    }
    FoilSurface surface;
    surface.base = base;
    surface.N = N;
    surface.V = V;
    surface.Lr = Lr;
    surface.uv0 = uv0;
    surface.puv = puv;
    surface.lightXY = lightXY;
    surface.glareGuard = glareGuard;
    surface.shine = shine;
    surface.foilMode = foilMode;
    surface.whiteMask = whiteMask;
    surface.relief = relief;
    surface.zoneFoil = zoneFoil;
    surface.bevel = bevel;
    surface.texel = uTexel;
    surface.tilt = uTilt;
    surface.fromCenter = uFromCenter;
    surface.sceneLit = uSceneLit;
    surface.depthDelta = uHasDepthMap > 0.5 ? texture2D(uDepthMap, puv).r - 0.5 : 0.0;
    vec3 outc = shadeFoilSurface(surface);

    // Board only: fold in the scene's lighting so the foil dims / warms with
    // the day-night cycle like its neighbouring lit cards. A simple wrapped
    // diffuse term on the relief normal (uLightColor) plus the flat ambient,
    // applied multiplicatively over the self-lit foil composite. uSceneLit=0
    // leaves the DOM look untouched.
    if (uSceneLit > 0.5) {
      float diffuse = clamp(dot(N, Lr) * 0.5 + 0.5, 0.0, 1.0);
      vec3 sceneShade = uAmbient + uLightColor * diffuse;
      outc *= sceneShade;
    }

    float alpha = 1.0;
    // uUseTexAlpha: take the card art's own alpha (its baked rounded-corner
    // transparency) as the output alpha. Used where the foil sits on a flat,
    // sharp quad with no rounded geometry to follow (the cemetery pile top face),
    // so its corners round EXACTLY like the non-foil card art instead of a
    // square opaque rect. Default 0 (opaque) everywhere else.
    if (uUseTexAlpha > 0.5) {
      alpha = texture2D(uTex, puv).a;
    } else if (uCornerRadius > 0.0) {
      // Rounded-rect coverage on vUv, measured in width units (y scaled by
      // the quad aspect) so the corner is circular on screen. uCornerRadius is
      // a fraction of the quad width; AA over a ~1.5px-ish band via fwidth.
      vec2 p = (vUv - 0.5) * vec2(1.0, uQuadAspect);
      vec2 q = abs(p) - (vec2(0.5, 0.5 * uQuadAspect) - uCornerRadius);
      float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - uCornerRadius;
      float aa = max(fwidth(d), 1e-4);
      alpha = 1.0 - smoothstep(0.0, aa, d);
    }
    vec3 outFinal = clamp(outc, 0.0, 1.0);
    // Board parity (uOutputLinear=1): the foil math runs on raw sRGB-byte values
    // (uTex sampled NoColorSpace, like the deck builder). The scene renderer
    // encodes linear->sRGB on output, so we sRGB-DECODE here to cancel that
    // encode and put the exact deck-builder result on screen. DOM callers leave
    // uOutputLinear=0 (their renderer outputs LinearSRGB, no encode to cancel).
    if (uOutputLinear > 0.5) {
      outFinal = mix(outFinal / 12.92, pow((outFinal + 0.055) / 1.055, vec3(2.4)), step(0.04045, outFinal));
    }
    gl_FragColor = vec4(outFinal, alpha * uOpacity);
  }
`;
